import React from "react";
import { Text, Stack } from "@chakra-ui/core";
import produce from "immer";
import Stepper from "@pency/components/inputs/Stepper";

import { Option, Variant } from "../../types";

import { usePrice } from "~/i18n/hooks";

interface Props {
  value?: Variant;
  limit: number;
  onChange: (value: Props["value"]) => void;
}

const MultiInput: React.FC<Props> = ({ limit, onChange, value, ...props }) => {
  const isFull = limit ? value?.value?.length! >= limit : false;
  const p = usePrice();

  function handleDecrease(option: Option) {
    const index =
      value?.value?.findIndex((selected) => selected.id === option.id) ?? -1;

    onChange(
      produce(value, (value) => {
        if (!value?.value) {
          value!.value = [];
        }

        value?.value.splice(index, 1);
      })
    );
  }

  function handleIncrease(option) {
    onChange({
      ...value,
      id: value?.id || "",
      title: value?.title ?? "", // Asegura que title sea siempre una cadena
      value: value?.value ? value.value.concat(option) : [option],
      count: value?.count ?? 0, // Proporciona un valor predeterminado de 0 si count es undefined
      required: value?.required ?? false,
      options: value?.options ?? [],
    });
  }

  return (
    <Stack shouldWrapChildren spacing={0} width="100%" {...props}>
      {value?.options
        ?.filter((option) => option.enabled)
        .map((option) => {
          const count = value?.value
            ? value.value.filter((selected) => selected.id === option.id).length
            : 0;

          // Calculate the effective max for this option considering stock
          const isOutOfStock =
            option.handleStock &&
            Number.isInteger(option.currentStock) &&
            option.currentStock === 0;

          const optionStockMax =
            option.handleStock && Number.isInteger(option.currentStock)
              ? option.currentStock!
              : undefined;

          // The effective max is the minimum of the variant limit and the option stock
          const effectiveMax = isFull
            ? count
            : optionStockMax !== undefined
            ? limit
              ? Math.min(limit, optionStockMax)
              : optionStockMax
            : limit || undefined;

          const isDisabled = isOutOfStock || (isFull && count === 0);

          return (
            <Stack
              key={option.id}
              isInline
              alignItems="center"
              borderBottomWidth={1}
              cursor={isDisabled ? "not-allowed" : "pointer"}
              height="auto"
              justifyContent="space-between"
              minHeight={12}
              opacity={isOutOfStock ? 0.5 : 1}
              paddingY={2}
              userSelect="none"
              onClick={() =>
                !isDisabled && option.enabled && handleIncrease(option)
              }
            >
              <Stack
                isInline
                shouldWrapChildren
                alignItems="center"
                spacing={2}
              >
                <Stepper
                  max={effectiveMax}
                  min={0}
                  value={count}
                  onDecrease={() => handleDecrease(option)}
                  onIncrease={() => !isOutOfStock && handleIncrease(option)}
                />
                <Text
                  color={
                    isOutOfStock
                      ? "faint"
                      : isFull
                      ? count
                        ? "text"
                        : "faint"
                      : "text"
                  }
                  fontWeight={count ? 500 : "normal"}
                >
                  {option.title}
                  {isOutOfStock && (
                    <Text as="span" color="red.400" fontSize="xs" ml={1}>
                      (Sin stock)
                    </Text>
                  )}
                </Text>
              </Stack>
              {Boolean(option.price) && (
                <Text
                  color="faint"
                  fontSize={{ base: "15px", sm: "sm" }}
                  fontWeight={500}
                >
                  + {p(option.price)}
                </Text>
              )}
            </Stack>
          );
        })}
    </Stack>
  );
};

export default MultiInput;
